import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Trash2, Globe, CheckCircle2, XCircle, Copy } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EVENTS = ["lead.created", "audit.completed", "payment.received", "lead.status_changed"];

export default function WebhooksTab({ adminToken }) {
  const [webhooks, setWebhooks] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUrl, setNewUrl] = useState("");
  const [creating, setCreating] = useState(false);
  const getHeaders = () => ({ Authorization: `Bearer ${adminToken}` });

  const fetch = useCallback(async () => {
    setLoading(true);
    const h = { Authorization: `Bearer ${adminToken}` };
    try {
      const [wRes, lRes] = await Promise.all([
        axios.get(`${API}/admin/webhooks`, { headers: h }),
        axios.get(`${API}/admin/webhooks/logs?limit=20`, { headers: h }),
      ]);
      setWebhooks(wRes.data.webhooks || []);
      setLogs(lRes.data.logs || []);
    } catch (err) { if (err.response?.status === 401) window.location.reload(); }
    setLoading(false);
  }, [adminToken]);

  useEffect(() => { fetch(); }, [fetch]);

  const createWebhook = async () => {
    if (!newUrl.trim()) return;
    setCreating(true);
    try {
      await axios.post(`${API}/admin/webhooks`, { url: newUrl, events: ["*"] }, { headers: getHeaders() });
      toast.success("Webhook created");
      setNewUrl("");
      fetch();
    } catch { toast.error("Failed to create webhook"); }
    setCreating(false);
  };

  const deleteWebhook = async (id) => {
    try {
      await axios.delete(`${API}/admin/webhooks/${id}`, { headers: getHeaders() });
      toast.success("Webhook deleted");
      fetch();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-6" data-testid="webhooks-tab">
      <div className="bg-white border rounded-lg p-5">
        <h3 className="text-sm font-bold text-slate-900 mb-1">Outbound Webhooks</h3>
        <p className="text-xs text-slate-500 mb-4">Send event payloads to external URLs (Zapier, Climbo, etc.) when leads are created, audits complete, or payments are received.</p>

        <div className="flex gap-2 mb-4">
          <Input placeholder="https://hooks.zapier.com/..." value={newUrl} onChange={e => setNewUrl(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") createWebhook(); }}
            className="text-sm flex-1" data-testid="webhook-url-input" />
          <Button size="sm" onClick={createWebhook} disabled={creating || !newUrl.trim()} data-testid="webhook-create-btn">
            {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            <span className="ml-1.5">Add</span>
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : webhooks.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">No webhooks configured. Add a URL above to start sending events.</p>
        ) : (
          <div className="space-y-2">
            {webhooks.map(w => (
              <div key={w.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border" data-testid={`webhook-${w.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Globe className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                    <p className="text-sm font-mono text-slate-700 truncate">{w.url}</p>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-[9px]">{w.enabled ? "Active" : "Disabled"}</Badge>
                    <span className="text-[10px] text-slate-400">Events: {(w.events || []).join(", ")}</span>
                  </div>
                  {w.secret && (
                    <button className="flex items-center gap-1 mt-1 text-[10px] text-slate-400 hover:text-slate-600"
                      onClick={() => { navigator.clipboard?.writeText(w.secret); toast.success("Secret copied"); }}>
                      <Copy className="w-3 h-3" /> Secret: {w.secret.slice(0, 8)}...
                    </button>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => deleteWebhook(w.id)} className="text-red-500 hover:text-red-700">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delivery logs */}
      <div className="bg-white border rounded-lg p-5">
        <h3 className="text-sm font-bold text-slate-900 mb-3">Recent Deliveries</h3>
        {logs.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">No webhook deliveries yet.</p>
        ) : (
          <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
            {logs.map((l, i) => (
              <div key={`${l.created_at}-${i}`} className="flex items-center gap-2 text-xs border-l-2 pl-3 py-1.5"
                style={{ borderColor: l.success ? "#059669" : "#dc2626" }}>
                {l.success ? <CheckCircle2 className="w-3 h-3 text-green-600 shrink-0" /> : <XCircle className="w-3 h-3 text-red-500 shrink-0" />}
                <Badge variant="outline" className="text-[9px]">{l.event}</Badge>
                <span className="text-slate-400">{l.status_code}</span>
                <span className="text-slate-500 truncate">{l.url}</span>
                <span className="text-slate-400 ml-auto shrink-0">{new Date(l.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Available events reference */}
      <div className="bg-slate-50 border rounded-lg p-4">
        <h4 className="text-xs font-bold text-slate-500 uppercase mb-2">Available Event Types</h4>
        <div className="flex flex-wrap gap-2">
          {EVENTS.map(e => <Badge key={e} variant="outline" className="text-[10px] font-mono">{e}</Badge>)}
        </div>
        <p className="text-[10px] text-slate-400 mt-2">Use * to subscribe to all events. Each delivery includes an X-Webhook-Signature header for verification.</p>
      </div>
    </div>
  );
}
